<template>
  <div class="item form-group mb-0" :class="{ failed: error }">
    <label v-if="label" :for="id" class="form-control-label">
      {{ label }}
      <span v-if="required">*</span>
    </label>
    <select
      :id="id"
      v-bind="$attrs"
      class="hidden"
      @input="$emit('update:modelValue', $event.target.value)"
    >
      <template v-for="option in options" :key="`${option.value}`">
        <optgroup
          :label="option.label"
          :key="`pg${option.value}`"
          v-if="option.options"
        >
          <option
            v-for="childOption in option.options"
            :key="`${childOption.value}`"
            :value="childOption.value"
            :selected="childOption.value === modelValue"
          >
            {{ childOption.label }}
          </option>
        </optgroup>
        <option
          :value="option.value"
          :selected="option.value === modelValue"
          v-else
        >
          {{ option.label }}
        </option>
      </template>
    </select>

    <div class="relative" ref="target">
      <button
        type="button"
        class="form-control form-control-select"
        :class="[
          error ? 'failed' : '',
          variant ? `form-control-${variant}` : ''
        ]"
        :disabled="disabled"
        @click="triggerDropdown"
      >
        {{ selectedLabel }}
      </button>

      <ul
        class="
          absolute
          top-20
          left-0
          w-full
          bg-white
          list-none
          max-h-140
          overflow-y-auto
          border-2px
          z-1
        "
        :class="[
          variant === 'dark' ? 'border-blue-900' : 'border-gray-400',
          { hidden: !showDropdown }
        ]"
      >
        <template v-for="option in options" :key="`${option.value}`">
          <li v-if="option.options">
            <div class="font-bold px-7 py-3">{{ option.label }}</div>
            <ul>
              <template
                v-for="childOption in option.options"
                :key="`${childOption.value}`"
              >
                <li
                  class="
                    cursor-pointer
                    transition-colors
                    duration-300
                    px-7
                    py-3
                    hover:text-white hover:bg-blue-900
                    selected:text-white selected:bg-blue-900
                  "
                  :class="{
                    selected: childOption.value === modelValue
                  }"
                  @click.prevent="changeValue($event, childOption.value)"
                >
                  {{ childOption.label }}
                </li>
              </template>
            </ul>
          </li>
          <li
            class="
              cursor-pointer
              transition-colors
              duration-300
              px-7
              py-3
              hover:text-white hover:bg-blue-900
              selected:text-white selected:bg-blue-900
            "
            :class="{
              selected: option.value === modelValue
            }"
            @click.prevent="changeValue($event, option.value)"
            v-else
          >
            {{ option.label }}
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';

export default {
  name: 'SelectField',
  props: {
    id: {
      type: String,
      default: Math.random().toString(36).substring(7)
    },
    label: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    rules: {
      type: Array,
      default: () => {
        return [];
      }
    },
    error: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    modelValue: [String, Number],
    variant: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const selectedLabel = ref('');

    onMounted(() => {
      if (!props.modelValue && props.defaultValue) {
        emit('update:modelValue', props.defaultValue.toString());
      } else if (!props.modelValue && props.options.length) {
        let first = [...props.options].shift();
        if (props.options[0].options) {
          first = [...props.options[0].options].shift();
        }
        emit('update:modelValue', first.value);
      }
    });

    const showDropdown = ref(false);

    const triggerDropdown = () => {
      showDropdown.value = !showDropdown.value;
    };

    const changeValue = (event, value) => {
      emit('update:modelValue', value);
      emit('change', event);
      showDropdown.value = false;
    };

    watch(
      () => props.modelValue,
      (value) => {
        if (value) {
          props.options.forEach((option) => {
            if (option.options) {
              option.options.forEach((option) => {
                if (option.value === props.modelValue) {
                  selectedLabel.value = option.label;
                }
              });
            } else if (option.value === props.modelValue) {
              selectedLabel.value = option.label;
            }
          });
        }
      },
      {
        immediate: true
      }
    );

    const target = ref(null);

    onClickOutside(target, () => {
      showDropdown.value = false;
    });

    const required = computed(() => {
      return props.rules.includes('required');
    });

    return {
      changeValue,
      triggerDropdown,
      showDropdown,
      selectedLabel,
      target,
      required
    };
  }
};
</script>
